import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

export type FinancialImpactFilter = "all" | "with" | "without"

export interface TicketTypeOption {
  id: number
  ticketName: string
}

export interface Ticket {
  id: string
  people_id: string | null
  person: string | null
  dsp: string | null
  ticket_name: string | null
  category: string | null
  sub_category: string | null
  details: string | null
  ticket_date_time: string | null
  amount: number | null
  created_by: string | null
  updated_by: string | null
  updated_at: string | null
  created_at: string | null
  tracking_id: string | null
  status: string | null
  route_code: string | null
  route_cycle: string | null
}

interface UseTicketsOptions {
  dateFrom?: string // inclusive ISO-like timestamp
  dateTo?: string // inclusive ISO-like timestamp
  searchQuery?: string
  financialImpactFilter?: FinancialImpactFilter
  statusFilter?: string // filter by status
  ticketTypeIdFilter?: number | null // filter by tickets.ticket_type_id
  driverManagerTeamFilter?: string // filter by people.sheriff_employer
}

interface BuildTicketsQueryParams {
  dateFrom?: string
  dateTo?: string
  searchQuery?: string
  financialImpactFilter?: FinancialImpactFilter
  statusFilter?: string
  ticketTypeIdFilter?: number | null
  driverManagerTeamFilter?: string
}

interface FetchTicketsParams {
  dateFrom?: string
  dateTo?: string
  searchQuery?: string
  financialImpactFilter?: FinancialImpactFilter
  statusFilter?: string
  ticketTypeIdFilter?: number | null
  driverManagerTeamFilter?: string
}

interface FetchTicketsResult {
  tickets: Ticket[]
  totalCount: number
}

interface TicketTypeRow {
  category?: string | null
  sub_category?: string | null
  ticket_name?: string | null
}

interface RawTicketRow {
  id: string
  people_id?: string | null
  person?: string | null
  dsp?: string | null
  details?: string | null
  ticket_date_time?: string | null
  amount?: number | null
  created_by?: string | null
  updated_by?: string | null
  updated_at?: string | null
  created_at?: string | null
  tracking_id?: string | null
  status?: string | null
  route_code?: string | null
  route_cycle?: string | null
  ticket_types?: TicketTypeRow | null
}

const buildSearchFilters = (searchTerm: string) => [
  `person.ilike.%${searchTerm}%`,
  `dsp.ilike.%${searchTerm}%`,
  `details.ilike.%${searchTerm}%`,
  `tracking_id.ilike.%${searchTerm}%`,
  `status.ilike.%${searchTerm}%`,
  `route_code.ilike.%${searchTerm}%`,
  `route_cycle.ilike.%${searchTerm}%`,
  `created_by.ilike.%${searchTerm}%`,
  `updated_by.ilike.%${searchTerm}%`,
]

/**
 * Builds a Supabase query for tickets with date filters, search, and ordering.
 * Does not include pagination (range) - that should be applied separately.
 */
export function buildTicketsQuery({
  dateFrom,
  dateTo,
  searchQuery = "",
  financialImpactFilter = "all",
  statusFilter = "",
  ticketTypeIdFilter = null,
  driverManagerTeamFilter = "",
}: BuildTicketsQueryParams) {
  const peopleJoin = driverManagerTeamFilter && driverManagerTeamFilter.trim() ? "!inner" : ""

  let query = supabase
    .from("tickets")
    .select(
      `
      id,
      people_id,
      person,
      dsp,
      ticket_type_id,
      details,
      ticket_date_time,
      amount,
      created_by,
      updated_by,
      updated_at,
      created_at,
      tracking_id,
      status,
      route_code,
      route_cycle,
      people:people_id${peopleJoin} (
        sheriff_employer
      ),
      ticket_types:ticket_type_id (
        category,
        sub_category,
        ticket_name
      )
    `,
      { count: "exact" },
    )
    .order("ticket_date_time", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false, nullsFirst: false })

  // Apply date range filter on ticket_date_time
  if (dateFrom) {
    query = query.gte("ticket_date_time", dateFrom)
  }
  if (dateTo) {
    query = query.lte("ticket_date_time", dateTo)
  }

  // Apply status filter
  if (statusFilter && statusFilter.trim()) {
    query = query.eq("status", statusFilter.trim())
  }

  // Apply driver manager team filter from related people table
  // (team is represented as people.sheriff_employer)
  if (driverManagerTeamFilter && driverManagerTeamFilter.trim()) {
    query = query.eq("people.sheriff_employer", driverManagerTeamFilter.trim())
  }

  // Filter on the base-table FK so no join constraint is needed
  if (ticketTypeIdFilter != null) {
    query = query.eq("ticket_type_id", ticketTypeIdFilter)
  }

  if (financialImpactFilter === "with") {
    query = query.not("amount", "is", null).neq("amount", 0)
  }

  // Apply text search across base table fields only
  // Note: Searching ticket_types fields (ticket_name, category, sub_category) is not supported
  // due to PostgREST limitations with foreign table filters in .or() conditions
  const trimmedSearchQuery = searchQuery.trim()
  const searchFilters = trimmedSearchQuery ? buildSearchFilters(trimmedSearchQuery) : []

  if (financialImpactFilter === "without") {
    if (searchFilters.length > 0) {
      const searchOr = `or(${searchFilters.join(",")})`
      query = query.or(`and(amount.is.null,${searchOr}),and(amount.eq.0,${searchOr})`)
    } else {
      query = query.or("amount.is.null,amount.eq.0")
    }
  } else if (searchFilters.length > 0) {
    query = query.or(searchFilters.join(","))
  }

  return query
}

/**
 * Transforms raw ticket data from Supabase (with nested ticket_types) into flat Ticket format.
 */
export function transformTickets(data: RawTicketRow[]): Ticket[] {
  return (data || []).map((ticket) => ({
    id: ticket.id,
    people_id: ticket.people_id ?? null,
    person: ticket.person,
    dsp: ticket.dsp,
    ticket_name: ticket.ticket_types?.ticket_name || null,
    category: ticket.ticket_types?.category || null,
    sub_category: ticket.ticket_types?.sub_category || null,
    details: ticket.details,
    ticket_date_time: ticket.ticket_date_time,
    amount: ticket.amount,
    created_by: ticket.created_by,
    updated_by: ticket.updated_by,
    updated_at: ticket.updated_at,
    created_at: ticket.created_at,
    tracking_id: ticket.tracking_id,
    status: ticket.status,
    route_code: ticket.route_code,
    route_cycle: ticket.route_cycle,
  }))
}

/**
 * Fetches all tickets matching the current filters.
 * RLS handles access control at the database level.
 */
export async function fetchTickets({
  dateFrom,
  dateTo,
  searchQuery = "",
  financialImpactFilter = "all",
  statusFilter = "",
  ticketTypeIdFilter = null,
  driverManagerTeamFilter = "",
}: FetchTicketsParams): Promise<FetchTicketsResult> {
  // Build query with filters
  const query = buildTicketsQuery({
    dateFrom,
    dateTo,
    searchQuery,
    financialImpactFilter,
    statusFilter,
    ticketTypeIdFilter,
    driverManagerTeamFilter,
  })

  // Execute query (RLS automatically filters based on user role/sub_teams)
  const { data, error, count } = await query

  if (error) {
    throw new Error(`Failed to fetch tickets: ${error.message}`)
  }

  // Transform the data to flatten ticket_types fields
  const tickets = transformTickets(data || [])
  const totalCount = count ?? 0

  return {
    tickets,
    totalCount,
  }
}

/**
 * Fetches only the total count for the current ticket filters.
 * Used to keep the selected-period summary accurate while the table pages by week.
 */
export async function fetchTicketsCount({
  dateFrom,
  dateTo,
  searchQuery = "",
  financialImpactFilter = "all",
  statusFilter = "",
  ticketTypeIdFilter = null,
  driverManagerTeamFilter = "",
}: BuildTicketsQueryParams): Promise<number> {
  const { error, count } = await buildTicketsQuery({
    dateFrom,
    dateTo,
    searchQuery,
    financialImpactFilter,
    statusFilter,
    ticketTypeIdFilter,
    driverManagerTeamFilter,
  }).range(0, 0)

  if (error) {
    throw new Error(`Failed to fetch ticket count: ${error.message}`)
  }

  return count ?? 0
}

/**
 * Fetches all tickets matching the filters in a single query (no pagination).
 * Used for CSV export where we need all matching tickets at once.
 * RLS handles access control at the database level.
 */
export async function fetchAllTickets({
  dateFrom,
  dateTo,
  searchQuery = "",
  financialImpactFilter = "all",
  statusFilter = "",
  ticketTypeIdFilter = null,
  driverManagerTeamFilter = "",
}: {
  dateFrom?: string
  dateTo?: string
  searchQuery?: string
  financialImpactFilter?: FinancialImpactFilter
  statusFilter?: string
  ticketTypeIdFilter?: number | null
  driverManagerTeamFilter?: string
}): Promise<Ticket[]> {
  // Build query with filters (no pagination)
  const query = buildTicketsQuery({
    dateFrom,
    dateTo,
    searchQuery,
    financialImpactFilter,
    statusFilter,
    ticketTypeIdFilter,
    driverManagerTeamFilter,
  })

  // Execute query (no range limit - fetches all matching records)
  // RLS automatically filters based on user role/sub_teams
  const { data, error } = await query

  if (error) {
    throw new Error(`Failed to fetch tickets: ${error.message}`)
  }

  // Transform the data to flatten ticket_types fields
  return transformTickets(data || [])
}

export function useTickets({
  dateFrom,
  dateTo,
  searchQuery = "",
  financialImpactFilter = "all",
  statusFilter = "",
  ticketTypeIdFilter = null,
  driverManagerTeamFilter = "",
}: UseTicketsOptions = {}) {
  return useQuery({
    queryKey: [
      "tickets",
      dateFrom,
      dateTo,
      searchQuery,
      financialImpactFilter,
      statusFilter,
      ticketTypeIdFilter,
      driverManagerTeamFilter,
    ],
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    queryFn: async () =>
      fetchTickets({
        dateFrom,
        dateTo,
        searchQuery,
        financialImpactFilter,
        statusFilter,
        ticketTypeIdFilter,
        driverManagerTeamFilter,
      }),
    enabled: !!(dateFrom && dateTo), // Only run query when both dates are provided
  })
}

export function useTicketsCount({
  dateFrom,
  dateTo,
  searchQuery = "",
  financialImpactFilter = "all",
  statusFilter = "",
  ticketTypeIdFilter = null,
  driverManagerTeamFilter = "",
}: UseTicketsOptions = {}) {
  return useQuery({
    queryKey: [
      "tickets-count",
      dateFrom,
      dateTo,
      searchQuery,
      financialImpactFilter,
      statusFilter,
      ticketTypeIdFilter,
      driverManagerTeamFilter,
    ],
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    queryFn: async () =>
      fetchTicketsCount({
        dateFrom,
        dateTo,
        searchQuery,
        financialImpactFilter,
        statusFilter,
        ticketTypeIdFilter,
        driverManagerTeamFilter,
      }),
    enabled: !!(dateFrom && dateTo),
  })
}

export function useTicketNameOptions() {
  return useQuery({
    queryKey: ["ticket-name-options"],
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<TicketTypeOption[]> => {
      const { data, error } = await supabase
        .from("ticket_types")
        .select("id, ticket_name")
        .order("ticket_name", { ascending: true })

      if (error) {
        throw new Error(`Failed to fetch ticket names: ${error.message}`)
      }

      return (data || []).map((row) => ({ id: row.id, ticketName: row.ticket_name }))
    },
  })
}
